import { useState } from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard, Package, Tags, ShoppingCart, Ticket, Truck, Settings, LogOut, Store, Menu, Heart, Users,
} from "lucide-react";

const LINKS = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/products", label: "Products", icon: Package },
  { to: "/admin/categories", label: "Categories", icon: Tags },
  { to: "/admin/orders", label: "Orders", icon: ShoppingCart },
  { to: "/admin/customers", label: "Customers", icon: Users },
  { to: "/admin/sellers", label: "Sellers", icon: Store },
  { to: "/admin/coupons", label: "Coupons", icon: Ticket },
  { to: "/admin/delivery", label: "Delivery", icon: Truck },
  { to: "/admin/gallery", label: "Happy Customers", icon: Heart },
  { to: "/admin/settings", label: "Settings", icon: Settings },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const doLogout = () => {
    logout();
    navigate("/admin/login");
  };

  const SidebarContent = () => (
    <>
      <Link to="/admin" className="flex items-center gap-2 px-6 py-5">
        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-brand text-ink">
          <Store className="h-5 w-5" />
        </span>
        <div>
          <p className="font-heading text-lg font-bold text-ink">Nicee</p>
          <p className="text-xs text-ink-muted">Admin Panel</p>
        </div>
      </Link>
      <nav className="flex-1 space-y-1 px-3">
        {LINKS.map((l) => (
          <NavLink
            key={l.to}
            to={l.to}
            end={l.end}
            onClick={() => setOpen(false)}
            data-testid={`admin-nav-${l.label.toLowerCase()}`}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-xl px-4 py-2.5 text-sm font-medium transition-colors ${
                isActive ? "bg-brand text-ink" : "text-ink-soft hover:bg-cream-alt"
              }`
            }
          >
            <l.icon className="h-4 w-4" /> {l.label}
          </NavLink>
        ))}
      </nav>
      <div className="border-t border-border p-4">
        <p className="px-2 text-xs text-ink-muted">Signed in as</p>
        <p className="px-2 text-sm font-semibold text-ink">{user?.name}</p>
        <Button variant="ghost" onClick={doLogout} className="mt-2 w-full justify-start rounded-xl text-ink-soft hover:text-destructive" data-testid="admin-logout">
          <LogOut className="mr-2 h-4 w-4" /> Log out
        </Button>
      </div>
    </>
  );

  return (
    <div className="flex min-h-screen bg-cream">
      <aside className="hidden w-64 flex-col border-r border-border bg-white lg:flex">
        <SidebarContent />
      </aside>

      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-ink/40" onClick={() => setOpen(false)} />
          <aside className="absolute left-0 top-0 flex h-full w-64 flex-col bg-white">
            <SidebarContent />
          </aside>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex items-center gap-3 border-b border-border bg-white px-4 py-3 lg:hidden">
          <Button variant="ghost" size="icon" onClick={() => setOpen(true)} data-testid="admin-menu-toggle">
            <Menu className="h-5 w-5" />
          </Button>
          <span className="font-heading font-bold text-ink">Admin</span>
        </div>
        <main className="flex-1 overflow-x-hidden p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
